import React from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import ContentArea from "../components/content-area/Content";
import { getBooths, deleteBooth } from '../api/boothApi';
import { getTeleportTable } from '../api/teleportApi';
import { getMapSpots } from '../api/spaceApi';
import { submitApproval } from '../api/approvalApi';
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { signalRService } from '../services/signalRService';
import { SIGNALR_EVENTS } from '../constants/signalREvents';
import { CONTENT_TYPES } from '../constants/contentTypes';
import PortalLinks from '../components/portals/PortalLinks';
import ConfirmModal from '../components/form-components/ConfirmModal';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { resolveBoothIcon } from '../utils/IconMap';
import { useLocalization } from "../context/LocalizationContext";
import LocalText from "../components/common/LocalText";
import { useSpaceId } from "../hooks/useSpaceId";
import ApprovalStatusBadge from '../components/approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../components/approval/SubmitForApprovalButton';
import WithdrawButton from '../components/approval/WithdrawButton';
import ContentPreviewDrawer from '../components/approval/ContentPreviewDrawer';
import EditBoothModal from '../components/booth/EditBoothModal';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';

function BoothEdit() {
  const { id } = useParams();
  const spaceId = useSpaceId();
  const [booth, setBooth] = useState(null);
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [proposeDeleteOpen, setProposeDeleteOpen] = useState(false);
  const [viewProposalOpen, setViewProposalOpen] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false });
  const [mapSpots, setMapSpots] = useState([]);
  const [allBooths, setAllBooths] = useState([]);
  const [teleportButtons, setTeleportButtons] = useState([]);
  const { locale, t } = useLocalization();

  const loadBooth = useCallback(async () => {
    if (!spaceId) return;
    try {
      const data = await getBooths();
      setAllBooths(data);
      setBooth(data.find((f) => String(f.id) === String(id)));
    } catch (err) {
      console.error('Failed to fetch booth:', err);
    }
  }, [id, spaceId]);

  useEffect(() => {
    loadBooth();
  }, [loadBooth]);

  useEffect(() => {
    getMapSpots().then((res) => setMapSpots(res.data ?? [])).catch(() => {});
  }, []);

  // A map spot already tied to another booth or a standalone teleport button must not
  // be pickable here either — fetch teleport buttons fresh whenever Edit modal opens.
  useEffect(() => {
    if (!editModal.open) return;
    getTeleportTable()
      .then((res) => {
        const buttons = Array.isArray(res.buttons) ? res.buttons : res.data?.buttons ?? [];
        setTeleportButtons(buttons);
      })
      .catch(console.error);
  }, [editModal.open]);

  // Editing this booth shouldn't disable the spot it already occupies.
  const usedMapSpotIds = useMemo(() => {
    const ids = new Set([
      ...allBooths.map((b) => b.mapSpotId),
      ...teleportButtons.map((b) => b.mapSpot?.id),
    ].filter(Boolean));
    if (booth?.mapSpotId != null) ids.delete(booth.mapSpotId);
    return ids;
  }, [allBooths, teleportButtons, booth]);

  // Refresh when an approval decision arrives for this booth, its portals, or its media
  useEffect(() => {
    const handleDecision = (payload) => {
      if (
        payload?.contentType === CONTENT_TYPES.BOOTH ||
        payload?.contentType === CONTENT_TYPES.PORTAL ||
        payload?.contentType === CONTENT_TYPES.BOOTH_MEDIA ||
        payload?.contentType === CONTENT_TYPES.CUSTOM_BOOTH_ITEM
      ) loadBooth();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [loadBooth]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (
        payload?.contentType === CONTENT_TYPES.BOOTH ||
        payload?.contentType === CONTENT_TYPES.PORTAL ||
        payload?.contentType === CONTENT_TYPES.BOOTH_MEDIA ||
        payload?.contentType === CONTENT_TYPES.CUSTOM_BOOTH_ITEM
      ) loadBooth();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [loadBooth]);

  const handleOnDelete = async () => {
    await deleteBooth(id);
    navigate('/booth');
  };

  const handleProposeDelete = async () => {
    try {
      await submitApproval({
        contentType: CONTENT_TYPES.BOOTH,
        contentId: booth.id,
        contentName: boothName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteOpen(false);
      await loadBooth();
    } catch {
      setProposeDeleteOpen(false);
    }
  };

  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/booth', 'booth-edit');

  const boothName =
    booth?.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
    booth?.localizedPairs?.values?.[0]?.value ||
    t(booth?.localizedPairs?.key, '') ||
    '';
  const boothIcon = resolveBoothIcon(booth);

  const isLocked      = booth?.approvalStatus === 'pending' || booth?.approvalStatus === 'rejected';
  const canDirectEdit = booth?.contentStatus === 'draft' && !isLocked;

  const viewApprovalRecord = booth?.approvalId ? {
    approvalId:      booth.approvalId,
    actionType:      booth.approvalActionType,
    contentType:     CONTENT_TYPES.BOOTH,
    sectionLabel:    'Booth',
    contentName:     boothName,
    submittedByName: '',
    submittedAt:     null,
    status:          booth.approvalStatus,
  } : null;

  // locked = pending/rejected — neither direct delete nor propose delete allowed
  // canDirectEdit = draft + no record → direct delete
  // !canDirectEdit && !isLocked = approved → propose delete via approval
  const deleteDisabled = isLocked;
  const deleteTooltip = isLocked
    ? booth?.approvalStatus === 'pending'
      ? 'A pending proposal exists — Withdraw it first'
      : 'A rejected proposal exists — Dismiss it first'
    : '';

  return (
    <ContentArea title={boothName || "—"} icon={boothIcon}>

      {/* Approval status bar */}
      {booth && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600 }}>
              Status:
            </Typography>
            <ApprovalStatusBadge
              status={booth.approvalStatus ?? booth.contentStatus}
              actionType={booth.approvalActionType}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {/* Not locked: direct edit (draft) or propose edit (approved) */}
            {canEdit && !isLocked && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditOutlinedIcon sx={{ fontSize: 13 }} />}
                onClick={() => setEditModal({ open: true, boothId: booth.id, initialData: booth, actionType: null, contentName: boothName, approvalMode: !canDirectEdit })}
                sx={{
                  borderColor: '#5383b3',
                  color: '#0a326d',
                  bgcolor: 'white',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.6,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#EAF0F6', borderColor: '#0a326d', boxShadow: 'none' },
                }}
              >
                {canDirectEdit ? 'Edit' : 'Propose Edit'}
              </Button>
            )}

            {/* Draft with no approval record: allow submit */}
            {canEdit && canDirectEdit && (
              <SubmitForApprovalButton
                contentType={CONTENT_TYPES.BOOTH}
                contentId={booth.id}
                contentName={boothName}
                onSubmitted={loadBooth}
              />
            )}

            {/* Locked: view proposal */}
            {isLocked && viewApprovalRecord && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                onClick={() => setViewProposalOpen(true)}
                sx={{
                  borderColor: '#3b82f6',
                  color: '#1d4ed8',
                  bgcolor: '#eff6ff',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.6,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
                }}
              >
                View Proposal
              </Button>
            )}

            {/* Pending: withdraw */}
            {canEdit && booth.approvalStatus === 'pending' && booth.approvalId && (
              <WithdrawButton approvalId={booth.approvalId} onWithdrawn={loadBooth} />
            )}

            {/* Rejected: dismiss */}
            {canEdit && booth.approvalStatus === 'rejected' && booth.approvalId && (
              <WithdrawButton approvalId={booth.approvalId} onWithdrawn={loadBooth} variant="dismiss" />
            )}
          </Box>
        </Box>
      )}

      {/* Portals panel */}
      <PortalLinks
        id={id}
        booth={booth}
        boothName={boothName}
        onBoothRefresh={loadBooth}
      />

      {/* Delete booth — guarded by approval state and edit permission */}
      {canEdit && (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Tooltip title={deleteTooltip} disableHoverListener={!deleteTooltip}>
          <span>
            <Button
              variant="contained"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
              disabled={deleteDisabled}
              onClick={(e) => { e.currentTarget.blur(); canDirectEdit ? setDeleteOpen(true) : setProposeDeleteOpen(true); }}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                bgcolor: canDirectEdit ? "#dc2626" : "#d97706",
                color: "white",
                fontWeight: 600,
                fontSize: 13,
                px: 2.5,
                py: 1,
                boxShadow: "none",
                "&:hover": { bgcolor: canDirectEdit ? "#b91c1c" : "#b45309", boxShadow: canDirectEdit ? "0 4px 12px rgba(220,38,38,0.35)" : "0 4px 12px rgba(217,119,6,0.35)" },
                "&.Mui-disabled": { bgcolor: "rgba(148,163,184,0.4)", color: "white" },
                transition: "all 0.2s ease",
              }}
            >
              {canDirectEdit ? <LocalText>Delete Booth</LocalText> : <LocalText>Propose Deletion</LocalText>}
            </Button>
          </span>
        </Tooltip>
      </Box>
      )}

      {/* Direct delete — only for draft + no approval record */}
      <ConfirmModal
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleOnDelete}
        severity="danger"
        title="Delete Booth"
        message="Are you sure you want to delete this booth?"
        subtitle="This action cannot be undone. All portals and media linked to this booth will be permanently removed."
        confirmLabel="Delete"
      />

      {/* Propose deletion — for approved booths, routes through approval */}
      <ConfirmModal
        open={proposeDeleteOpen}
        onCancel={() => setProposeDeleteOpen(false)}
        onConfirm={handleProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${boothName}"?`}
        subtitle="The booth remains live until an approver approves the deletion."
        confirmLabel="Submit for Approval"
      />

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={booth?.approvalStatus === 'rejected' ? (snap) => {
            setViewProposalOpen(false);
            setEditModal({ open: true, boothId: booth.id, initialData: snap, actionType: booth.approvalActionType, contentName: boothName, approvalMode: false });
          } : undefined}
        />
      )}

      <EditBoothModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false })}
        boothId={editModal.boothId}
        initialData={editModal.initialData}
        actionType={editModal.actionType}
        contentName={editModal.contentName}
        approvalMode={editModal.approvalMode}
        usedMapSpotIds={usedMapSpotIds}
        types={mapSpots}
        onSaved={() => {
          setEditModal({ open: false, boothId: null, initialData: null, actionType: null, contentName: null, approvalMode: false });
          loadBooth();
        }}
      />
    </ContentArea>
  );
}

export default BoothEdit;
